import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9CA3AF;

  &:hover {
    color: #6B7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #686868;
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #CFD3D4;
  font-weight: 400;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #CFD3D4;
  font-weight: 400;
  font-family: 'Prompt', sans-serif;
  resize: vertical;

  &::placeholder {
    color: #CFD3D4;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 16px;
  color: #CFD3D4;
  font-weight: 400;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const RadioInput = styled.input`
  accent-color: #00B5B8;
`;

const RadioText = styled.span`
  font-size: 16px;
  color: #686868;
  font-weight: 400;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ImagePreview = styled.img`
  margin-top: 1rem;
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
`;

const MapContainer = styled.div`
  margin-bottom: 1.5rem;
  height: 300px;
  background-color: #E5E7EB;
  border-radius: 6px;
  overflow: hidden;
`;

const NoticeText = styled.div`
  margin-bottom: 1.5rem;
  font-size: 14px;
  color: #686868;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #E5E7EB;
  position: sticky;
  bottom: 0;
  background-color: white;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.875rem;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #009B9E;
  }
`;

const CampaignManagementModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    campaignName: '',
    campaignDescription: '',
    serviceType: 'hospital',
    campaignDuration: '',
    startDate: '',
    endDate: '',
    hasCost: false,
    cost: '',
    promoCode: '',
    company: '',
    latitude: '',
    longitude: '',
    deliveryLocation: '',
    address: '',
    postalCode: '',
    subdistrictCode: '',
    note: ''
  });

  const [selectedCampaignImage, setSelectedCampaignImage] = useState(null);
  const [selectedCampaignIcon, setSelectedCampaignIcon] = useState(null);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedCampaignImage(URL.createObjectURL(file));
    }
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedCampaignIcon(URL.createObjectURL(file));
    }
  };

  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    onClose();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <ModalTitle>จัดการแคมเปญ</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {/* เลือกรูปภาพ */}
          <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('image-upload').click()}>
              เลือกรูปภาพ
            </ImageUploadButton>
            {selectedCampaignImage && <ImagePreview src={selectedCampaignImage} alt="Campaign Preview" />}
          </ImageUploadSection>

          {/* เลือกไอคอน */}
          <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="icon-upload"
              accept="image/*"
              onChange={handleIconUpload}
            />
            <ImageUploadButton onClick={() => document.getElementById('icon-upload').click()}>
              เลือกไอคอน
            </ImageUploadButton>
            {selectedCampaignIcon && <ImagePreview src={selectedCampaignIcon} alt="Icon Preview" />}
          </ImageUploadSection>

          {/* ชื่อแคมเปญ */}
          <FormGroup>
            <Label>ชื่อแคมเปญ</Label>
            <Input
              type="text"
              value={formData.campaignName}
              onChange={(e) => handleInputChange('campaignName', e.target.value)}
              placeholder="ชื่อแคมเปญ"
            />
          </FormGroup>

          {/* รายละเอียดแคมเปญ */}
          <FormGroup>
            <Label>รายละเอียดแคมเปญ</Label>
            <TextArea
              value={formData.campaignDescription}
              onChange={(e) => handleInputChange('campaignDescription', e.target.value)}
              placeholder="รายละเอียดแคมเปญ"
              rows={3}
            />
          </FormGroup>

          {/* แคมเปญสำหรับหาหมอออนไลน์ */}
          <FormGroup>
            <Label>แคมเปญสำหรับหาหมอออนไลน์</Label>
            <RadioGroup>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="serviceType"
                  value="hospital"
                  checked={formData.serviceType === 'hospital'}
                  onChange={(e) => handleInputChange('serviceType', e.target.value)}
                />
                <RadioText>หาหมอที่โรงพยาบาล</RadioText>
              </RadioLabel>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="serviceType"
                  value="online"
                  checked={formData.serviceType === 'online'}
                  onChange={(e) => handleInputChange('serviceType', e.target.value)}
                />
                <RadioText>หาหมออนไลน์</RadioText>
              </RadioLabel>
            </RadioGroup>
          </FormGroup>

          {/* ระยะแคมเปญกรณีรายบุคคล */}
          <FormGroup>
            <Label>ระยะแคมเปญกรณีรายบุคคล</Label>
            <Select
              value={formData.campaignDuration}
              onChange={(e) => handleInputChange('campaignDuration', e.target.value)}
            >
              <option value="">เลือกจำนวนวัน</option>
              <option value="7">7 วัน</option>
              <option value="14">14 วัน</option>
              <option value="30">30 วัน</option>
              <option value="60">60 วัน</option>
              <option value="90">90 วัน</option>
            </Select>
          </FormGroup>

          {/* วันที่เริ่มต้น และ วันที่สิ้นสุด */}
          <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่เริ่มต้น ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                placeholder="2024-01-01"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>วันที่สิ้นสุด ค.ศ. (ปปปป-ดด-วว)</Label>
              <Input
                type="text"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                placeholder="2024-12-31"
              />
            </FormGroup>
          </GridContainer>

          {/* ค่าใช้จ่าย */}
          <FormGroup>
            <Label>ค่าใช้จ่าย</Label>
            <RadioGroup>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="hasCost"
                  checked={!formData.hasCost}
                  onChange={() => handleInputChange('hasCost', false)}
                />
                <RadioText>ไม่มีค่าใช้จ่าย</RadioText>
              </RadioLabel>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="hasCost"
                  checked={formData.hasCost}
                  onChange={() => handleInputChange('hasCost', true)}
                />
                <RadioText>มีค่าใช้จ่าย ระบุ</RadioText>
              </RadioLabel>
            </RadioGroup>
            {formData.hasCost && (
              <div style={{ marginTop: '0.75rem' }}>
                <Select
                  value={formData.cost}
                  onChange={(e) => handleInputChange('cost', e.target.value)}
                >
                  <option value="">เลือกค่าใช้จ่าย</option>
                  <option value="100">100 บาท</option>
                  <option value="200">200 บาท</option>
                  <option value="300">300 บาท</option>
                  <option value="500">500 บาท</option>
                  <option value="1000">1,000 บาท</option>
                </Select>
              </div>
            )}
          </FormGroup>

          {/* ระบุรหัสโปรโมชั่น */}
          <FormGroup>
            <Label>ระบุรหัสโปรโมชั่น ตัวเลข 4 ตัว ใช้สำหรับแจ้งให้คนไข้ทราบเพื่อที่คนไข้ไม่ต้องชำระเงิน</Label>
            <Input
              type="text"
              value={formData.promoCode}
              onChange={(e) => handleInputChange('promoCode', e.target.value)}
              placeholder="0000"
              maxLength={4}
            />
          </FormGroup>

          {/* บริษัท */}
          <FormGroup>
            <Label>บริษัท</Label>
            <Select
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
            >
              <option value="">เลือกบริษัท</option>
              <option value="company1">บริษัท A</option>
              <option value="company2">บริษัท B</option>
              <option value="company3">บริษัท C</option>
            </Select>
          </FormGroup>

          {/* Google Map */}
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.5!2d100.641032278634!3d13.654467997983!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDM5JzE2LjEiTiAxMDDCsDM4JzI3LjciRQ!5e0!3m2!1sth!2sth!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </MapContainer>

          {/* ข้อความแจ้งเตือน */}
          <NoticeText>
            ระบบจำเป็นต้องขอที่อยู่จัดส่งยาจากท่านเพื่อจัดหาจุดบริการที่ใกล้เคียงกับท่านมากที่สุด
          </NoticeText>

          {/* ละติจูด และ ลองจิจูด */}
          <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ละติจูด</Label>
              <Input
                type="text"
                value={formData.latitude}
                onChange={(e) => handleInputChange('latitude', e.target.value)}
                placeholder="13.654467997983"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ลองจิจูด</Label>
              <Input
                type="text"
                value={formData.longitude}
                onChange={(e) => handleInputChange('longitude', e.target.value)}
                placeholder="100.641032278634"
              />
            </FormGroup>
          </GridContainer>

          {/* ชื่อสถานที่จัดส่งยา */}
          <FormGroup>
            <Label>ชื่อสถานที่จัดส่งยา</Label>
            <Input
              type="text"
              value={formData.deliveryLocation}
              onChange={(e) => handleInputChange('deliveryLocation', e.target.value)}
              placeholder="ชื่อสถานที่จัดส่งยา"
            />
          </FormGroup>

          {/* ที่อยู่ */}
          <FormGroup>
            <Label>ที่อยู่</Label>
            <TextArea
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="ที่อยู่"
              rows={3}
            />
          </FormGroup>

          {/* รหัสไปรษณีย์ */}
          <FormGroup>
            <Label>รหัสไปรษณีย์</Label>
            <Input
              type="text"
              value={formData.postalCode}
              onChange={(e) => handleInputChange('postalCode', e.target.value)}
              placeholder="รหัสไปรษณีย์"
            />
          </FormGroup>

          {/* รหัสตำบล */}
          <FormGroup>
            <Label>รหัสตำบล</Label>
            <Input
              type="text"
              value={formData.subdistrictCode}
              onChange={(e) => handleInputChange('subdistrictCode', e.target.value)}
              placeholder="รหัสตำบล"
            />
          </FormGroup>

          {/* หมายเหตุ */}
          <FormGroup>
            <Label>หมายเหตุ</Label>
            <TextArea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="หมายเหตุ"
              rows={3}
            />
          </FormGroup>
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <SubmitButton onClick={handleSubmit}>
            เพิ่ม
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CampaignManagementModal;
